export type RoleBadgeVariant =
  | "soft-primary"
  | "soft-success"
  | "soft-warning"
  | "soft-info"
  | "soft-error";

export function normalizeRole(value: unknown): string {
  if (typeof value !== "string") return "";
  const compact = value.replace(/[\s_-]+/g, "").toLowerCase();
  if (compact === "storemanager") return "StoreManager";
  if (compact === "admin") return "Admin";
  if (compact === "merchant") return "Merchant";
  if (compact === "user") return "User";
  return value.trim();
}

export function getRoleBadgeVariant(role: unknown): RoleBadgeVariant {
  const normalized = normalizeRole(role);
  if (normalized === "Admin") return "soft-error";
  if (normalized === "StoreManager") return "soft-warning";
  if (normalized === "Merchant") return "soft-primary";
  if (normalized === "User") return "soft-info";
  return "soft-info";
}

export function isAdminRole(role: string | undefined): boolean {
  return role === "Admin";
}

export function isStoreManagerRole(role: string | undefined): boolean {
  return role === "StoreManager";
}

export function isMerchantRole(role: string | undefined): boolean {
  return !!role && !isAdminRole(role) && !isStoreManagerRole(role);
}

export interface OrderRoleCapabilities {
  canCreateOrder: boolean;
  canUpdateOrder: boolean;
  canRunBulkActions: boolean;
  canFilterByMerchant: boolean;
  /** Compose/edit comments. Reading is open to every role — the backend scopes and redacts. */
  canManageComments: boolean;
}

export function getOrderRoleCapabilities(role: string | undefined): OrderRoleCapabilities {
  const isAdmin = isAdminRole(role);
  const isStoreManager = isStoreManagerRole(role);
  const isMerchant = isMerchantRole(role);

  return {
    canCreateOrder: isStoreManager || isMerchant,
    canUpdateOrder: isStoreManager || isMerchant,
    canRunBulkActions: isStoreManager || isMerchant,
    canFilterByMerchant: isAdmin,
    canManageComments: isAdmin,
  };
}

export interface PackageTypeRoleCapabilities {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** Admin needs a merchant picker since the list isn't auto-scoped server-side. */
  canFilterByMerchant: boolean;
}

export function getPackageTypeRoleCapabilities(
  role: string | undefined
): PackageTypeRoleCapabilities {
  const isAdmin = isAdminRole(role);
  return {
    canCreate: true,
    canEdit: true,
    canDelete: true,
    canFilterByMerchant: isAdmin,
  };
}
